import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, vModelSelect as _vModelSelect, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "sync-panel",
  "aria-labelledby": "sync-title"
}
const _hoisted_2 = { id: "sync-title" }
const _hoisted_3 = { class: "sync-check" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { value: "both" }
const _hoisted_7 = { value: "to-desktop" }
const _hoisted_8 = { value: "to-browser" }
const _hoisted_9 = { class: "sync-buttons" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  role: "status"
}
const _hoisted_12 = { class: "sync-path" }
const _hoisted_13 = {
  key: 1,
  role: "alert",
  class: "sync-error"
}
const _hoisted_14 = { class: "sync-summary" }
const _hoisted_15 = { class: "sync-buttons" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "sync-table" }
const _hoisted_20 = { class: "sync-path" }
const _hoisted_21 = ["onUpdate:modelValue", "aria-label", "disabled"]
const _hoisted_22 = { value: "skip" }
const _hoisted_23 = {
  key: 0,
  value: "browser"
}
const _hoisted_24 = {
  key: 1,
  value: "desktop"
}
const _hoisted_25 = { key: 1 }
const _hoisted_26 = ["disabled"]
const _hoisted_27 = {
  key: 3,
  class: "sync-result",
  role: "status"
}
const _hoisted_28 = {
  key: 0,
  class: "sync-error"
}
const _hoisted_29 = { class: "sync-note" }

import { computed, inject, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { BrowserStorage } from '../services/storage/browserStorage';
import { DesktopStorage, rememberDesktopConnection, type StorageSettings } from '../services/storage/desktopStorage';
import type { StorageClass } from '../services/storage/storage';
import { compareStorage, executeSync, planSync, type SyncChoice, type SyncDirection, type SyncResult, type SyncRow } from '../services/storage/storageSync';

export default /*@__PURE__*/_defineComponent({
  __name: 'StorageSyncPanel',
  props: {
    settings: {}
  },
  emits: ["busy", "locked"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const storage = inject<StorageClass>('storage')!;
const { locale } = useI18n({ useScope: 'global' });
const acknowledged = ref(false), busy = ref(false), error = ref(''), completed = ref(0), currentPath = ref('');
const phase = ref<'scan' | 'sync'>('scan'), limit = ref(200), direction = ref<SyncDirection>('both');
const rows = ref<SyncRow[]>(), result = shallowRef<SyncResult>(), controller = shallowRef<AbortController>();
let browser: BrowserStorage | undefined, desktop: DesktopStorage | undefined;
let paused = false, scanEndpoint = '', scanToken = '';
const statuses: SyncRow['status'][] = ['browser-only', 'desktop-only', 'same', 'conflict', 'blocked'];
const actions = computed(() => planSync(rows.value || [], direction.value));
const changedRows = computed(() => (rows.value || []).filter(row => row.status !== 'same'));
const count = (status: SyncRow['status']) => (rows.value || []).filter(row => row.status === status).length;
watch(busy, value => emit('busy', value), { flush: 'sync' });
watch(() => [props.settings.endpoint, props.settings.token], () => { if (!busy.value) { rows.value = undefined; result.value = undefined; } });
onBeforeUnmount(() => { controller.value?.abort(); desktop?.dispose(); });
const messages = {
  title: ['迁移与双向同步', 'Migrate and sync'],
  intro: ['先比较已保存的存档，再执行复制。双向合并补齐各自缺少的文件；同名但内容不同的文件由你选择。比较成功后记住连接，不改变当前保存位置。', 'Compare saved files before copying. Merge fills missing files on both sides; choose a version for conflicting files. A successful comparison remembers the connection without changing your save location.'],
  ack: ['我已保存或导出当前编辑，并关闭其他 UGC Tools 编辑页面。', 'I saved or exported my edits and closed other UGC Tools editor tabs.'],
  direction: ['同步方向', 'Direction'], both: ['双向合并', 'Merge both ways'], toDesktop: ['浏览器 → 电脑', 'Browser → Desktop'], toBrowser: ['电脑 → 浏览器', 'Desktop → Browser'],
  compare: ['比较两边存档', 'Compare saves'], cancel: ['取消（当前文件完成后停止）', 'Cancel after current file'], scan: ['正在比较', 'Comparing'], sync: ['正在同步', 'Syncing'],
  'browser-only': ['仅浏览器', 'Browser only'], 'desktop-only': ['仅电脑', 'Desktop only'], same: ['一致', 'Identical'], conflict: ['内容冲突', 'Content conflict'], blocked: ['文件/目录冲突', 'File/folder conflict'],
  conflictHint: ['冲突默认跳过。选择使用哪一边后，会备份并覆盖另一边的同名文件。文件/目录冲突需先重命名处理。', 'Conflicts are skipped by default. Choosing a version backs up and replaces the other side. Rename file/folder conflicts before syncing.'],
  allBrowser: ['所有冲突使用浏览器版本', 'Use browser for all conflicts'], allDesktop: ['所有冲突使用电脑版本', 'Use desktop for all conflicts'], allSkip: ['跳过所有冲突', 'Skip all conflicts'],
  path: ['文件路径', 'Path'], difference: ['差异', 'Difference'], action: ['处理方式', 'Action'], skip: ['跳过', 'Skip'], useBrowser: ['使用浏览器版本', 'Use browser version'], useDesktop: ['使用电脑版本', 'Use desktop version'],
  more: ['显示更多', 'Show more'], planned: ['将复制', 'Will copy'], items: ['项（含文件夹）', 'items (including folders)'], run: ['执行上述同步', 'Run this sync'],
  failed: ['同步已停止，部分操作可能已完成', 'Sync stopped; some operations may have completed'], cancelled: ['同步已取消', 'Sync cancelled'], done: ['所选同步操作已完成', 'Selected sync operations completed'],
  after: ['已完成的复制会保留。请刷新页面重新读取存档；未处理的冲突或失败项可在刷新后重新比较。', 'Completed copies are retained. Reload to read the saves, then compare again for any unresolved conflicts or failures.'],
  reload: ['刷新并读取存档', 'Reload saves'],
  scope: ['同步所有项目存档及空文件夹，不传播删除，不同步回收站、内部备份或浏览器偏好。电脑覆盖前使用历史备份，浏览器备份在 /.ugc-sync-backups/。这是手动同步，不会在后台持续运行。', 'Includes project saves and empty folders. No deletion propagation, trash, internal backups or browser preferences. Replaced desktop files use history; browser backups go to /.ugc-sync-backups/. Runs manually, not continuously.'],
} as const;
const t = (key: keyof typeof messages) => messages[key][locale.value.startsWith('zh') ? 0 : 1];
function actionLabel(row: SyncRow) { const action = planSync([row], direction.value)[0]; return action ? t(action.from === 'browser' ? 'toDesktop' : 'toBrowser') : t('skip'); }
function chooseAll(choice: SyncChoice) { for (const row of rows.value || []) if (row.status === 'conflict') row.choice = choice; }
function reload() { window.location.reload(); }
async function compare() {
  busy.value = true; phase.value = 'scan'; error.value = ''; result.value = undefined; rows.value = undefined; completed.value = 0; limit.value = 200;
  controller.value = new AbortController();
  try {
    scanEndpoint = props.settings.endpoint; scanToken = props.settings.token;
    desktop?.dispose(); desktop = new DesktopStorage({ ...props.settings }); await desktop.prepareSync();
    browser ??= new BrowserStorage(); await browser.init();
    const compared = await compareStorage(browser, desktop, path => { currentPath.value = path; completed.value++; }, controller.value.signal);
    rememberDesktopConnection(props.settings);
    rows.value = compared;
  } catch (cause) { error.value = cause instanceof Error ? cause.message : String(cause); }
  finally { busy.value = false; }
}
async function run() {
  if (!browser || !desktop || !actions.value.length) return;
  busy.value = true; phase.value = 'sync'; error.value = ''; completed.value = 0; controller.value = new AbortController();
  try {
    if (scanEndpoint !== props.settings.endpoint || scanToken !== props.settings.token) throw new Error('连接设置已改变，请重新比较。');
    if (!paused) { await storage.pauseForSync(); paused = true; }
    emit('locked');
    result.value = await executeSync(actions.value, browser, desktop, (done, path) => { completed.value = done; currentPath.value = path; }, controller.value.signal);
    // Keep editors paused after even partial/uncertain writes until a reload.
  } catch (cause) { error.value = cause instanceof Error ? cause.message : String(cause); }
  finally { busy.value = false; }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h3", _hoisted_2, _toDisplayString(t('title')), 1),
    _createElementVNode("p", null, _toDisplayString(t('intro')), 1),
    _createElementVNode("label", _hoisted_3, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((acknowledged).value = $event)),
        type: "checkbox",
        disabled: busy.value
      }, null, 8, _hoisted_4), [
        [_vModelCheckbox, acknowledged.value]
      ]),
      _createTextVNode(_toDisplayString(t('ack')), 1)
    ]),
    _createElementVNode("label", null, [
      _createTextVNode(_toDisplayString(t('direction')) + " ", 1),
      _withDirectives(_createElementVNode("select", {
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((direction).value = $event)),
        disabled: busy.value
      }, [
        _createElementVNode("option", _hoisted_6, _toDisplayString(t('both')), 1),
        _createElementVNode("option", _hoisted_7, _toDisplayString(t('toDesktop')), 1),
        _createElementVNode("option", _hoisted_8, _toDisplayString(t('toBrowser')), 1)
      ], 8, _hoisted_5), [
        [_vModelSelect, direction.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("button", {
        type: "button",
        disabled: busy.value || !acknowledged.value,
        onClick: compare
      }, _toDisplayString(t('compare')), 9, _hoisted_10),
      (busy.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (controller.value?.abort()))
          }, _toDisplayString(t('cancel')), 1))
        : _createCommentVNode("", true)
    ]),
    (busy.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_11, [
          _createTextVNode(_toDisplayString(t(phase.value)) + " " + _toDisplayString(completed.value), 1),
          _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
          _createElementVNode("span", _hoisted_12, _toDisplayString(currentPath.value), 1)
        ]))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (rows.value && !result.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          _createElementVNode("div", _hoisted_14, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statuses, (status) => {
              return _createElementVNode("span", { key: status }, _toDisplayString(t(status)) + ": " + _toDisplayString(count(status)), 1)
            }), 64))
          ]),
          (count('conflict'))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("p", null, _toDisplayString(t('conflictHint')), 1),
                _createElementVNode("div", _hoisted_15, [
                  (direction.value !== 'to-browser')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        disabled: busy.value,
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (chooseAll('browser')))
                      }, _toDisplayString(t('allBrowser')), 9, _hoisted_16))
                    : _createCommentVNode("", true),
                  (direction.value !== 'to-desktop')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        disabled: busy.value,
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (chooseAll('desktop')))
                      }, _toDisplayString(t('allDesktop')), 9, _hoisted_17))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    type: "button",
                    disabled: busy.value,
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (chooseAll('skip')))
                  }, _toDisplayString(t('allSkip')), 9, _hoisted_18)
                ])
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("table", null, [
              _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", null, _toDisplayString(t('path')), 1),
                  _createElementVNode("th", null, _toDisplayString(t('difference')), 1),
                  _createElementVNode("th", null, _toDisplayString(t('action')), 1)
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(changedRows.value.slice(0, limit.value), (row) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: row.path
                  }, [
                    _createElementVNode("td", _hoisted_20, _toDisplayString(row.path), 1),
                    _createElementVNode("td", null, _toDisplayString(t(row.status)), 1),
                    _createElementVNode("td", null, [
                      (row.status === 'conflict')
                        ? _withDirectives((_openBlock(), _createElementBlock("select", {
                            key: 0,
                            "onUpdate:modelValue": ($event: any) => ((row.choice) = $event),
                            "aria-label": t('action') + ' ' + row.path,
                            disabled: busy.value
                          }, [
                            _createElementVNode("option", _hoisted_22, _toDisplayString(t('skip')), 1),
                            (direction.value !== 'to-browser')
                              ? (_openBlock(), _createElementBlock("option", _hoisted_23, _toDisplayString(t('useBrowser')), 1))
                              : _createCommentVNode("", true),
                            (direction.value !== 'to-desktop')
                              ? (_openBlock(), _createElementBlock("option", _hoisted_24, _toDisplayString(t('useDesktop')), 1))
                              : _createCommentVNode("", true)
                          ], 8, _hoisted_21)), [
                            [_vModelSelect, row.choice]
                          ])
                        : (_openBlock(), _createElementBlock("span", _hoisted_25, _toDisplayString(actionLabel(row)), 1))
                    ])
                  ]))
                }), 128))
              ])
            ])
          ]),
          (changedRows.value.length > limit.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 1,
                type: "button",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (limit.value += 200))
              }, _toDisplayString(t('more')), 1))
            : _createCommentVNode("", true),
          _createElementVNode("p", null, _toDisplayString(t('planned')) + " " + _toDisplayString(actions.value.length) + " " + _toDisplayString(t('items')), 1),
          _createElementVNode("button", {
            type: "button",
            class: "sync-primary",
            disabled: busy.value || !actions.value.length || !acknowledged.value,
            onClick: run
          }, _toDisplayString(t('run')), 9, _hoisted_26)
        ], 64))
      : _createCommentVNode("", true),
    (result.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
          _createElementVNode("strong", null, _toDisplayString(t(result.value.error ? 'failed' : result.value.cancelled ? 'cancelled' : 'done')), 1),
          _createElementVNode("p", null, _toDisplayString(result.value.completed) + " / " + _toDisplayString(result.value.total) + " " + _toDisplayString(t('items')), 1),
          (result.value.error)
            ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(result.value.failedPath) + ": " + _toDisplayString(result.value.error), 1))
            : _createCommentVNode("", true),
          _createElementVNode("p", null, _toDisplayString(t('after')), 1),
          _createElementVNode("button", {
            type: "button",
            class: "sync-primary",
            onClick: reload
          }, _toDisplayString(t('reload')), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("p", _hoisted_29, _toDisplayString(t('scope')), 1)
  ]))
}
}

})